import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Badge, BadgeVariant } from '@ui/components/base/badge';
import { Button, ButtonVariant } from '@ui/components/base/button';
import { Div } from '@ui/components/base/div';
import { Skeleton } from '@ui/components/base/skeleton';
import { Typography, TypographyVariant } from '@ui/components/base/typography';
import { LoaderButton } from '@ui/components/buttons/loaderButton';
import { ArrowLeftIcon, CheckIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

const RAIL_WIDTH = 232;
const BADGE_SIZE = 28;
const CONNECTOR_LEFT = BADGE_SIZE / 2 - 0.5;
const CONNECTOR_TOP = BADGE_SIZE + 2;
const CONNECTOR_BOTTOM = 6;
const CONTENT_MAX_WIDTH = 620;
const PAGE_MAX_WIDTH = 1024;
const WIDE_PAGE_MAX_WIDTH = 1280;
const HEADER_GUTTER_COMPENSATION = 8;

const SKELETON_STEP_COUNT = 4;
const SKELETON_FIELD_COUNT = 3;
const SKELETON_STEP_INDEXES = [...Array(SKELETON_STEP_COUNT).keys()];
const SKELETON_FIELD_INDEXES = [...Array(SKELETON_FIELD_COUNT).keys()];

const WizardPageSkeleton = () => (
  <Div column className="min-h-screen bg-canvas">
    <Div row className="h-16 shrink-0 items-center border-b border-border bg-background px-8">
      <Div
        row
        className="mx-auto w-full items-center gap-3.5"
        style={{ maxWidth: PAGE_MAX_WIDTH - HEADER_GUTTER_COMPENSATION }}
      >
        <Skeleton className="size-9 rounded-full" />
        <Skeleton className="h-5 w-52" />
      </Div>
    </Div>
    <Div
      grid
      className="mx-auto w-full flex-1 items-start gap-10 px-8 pt-12 pb-14"
      style={{ gridTemplateColumns: `${RAIL_WIDTH}px 1fr`, maxWidth: PAGE_MAX_WIDTH }}
    >
      <Div
        column
        className="gap-5 rounded-2xl border border-border bg-background px-5 pt-5 pb-3"
        style={{ boxShadow: 'var(--shadow-1)' }}
      >
        {SKELETON_STEP_INDEXES.map((stepIndex) => (
          <Div key={stepIndex} grid className="gap-3 pb-2" style={{ gridTemplateColumns: `${BADGE_SIZE}px 1fr` }}>
            <Skeleton className="size-7 rounded-full" />
            <Div column className="gap-1.5 pt-1">
              <Skeleton className="h-3.5 w-24" />
              <Skeleton className="h-3 w-16" />
            </Div>
          </Div>
        ))}
      </Div>
      <Div
        column
        className="min-w-0 rounded-2xl border border-border bg-background px-10 pt-9 pb-8"
        style={{ boxShadow: 'var(--shadow-1)' }}
      >
        <Div column className="gap-2.5" style={{ maxWidth: CONTENT_MAX_WIDTH }}>
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-6 w-72" />
          <Skeleton className="h-4 w-full max-w-130" />
        </Div>
        <Div column className="gap-6 pt-8" style={{ maxWidth: CONTENT_MAX_WIDTH }}>
          {SKELETON_FIELD_INDEXES.map((fieldIndex) => (
            <Div key={fieldIndex} column className="gap-2">
              <Skeleton className="h-3.5 w-28" />
              <Skeleton className="h-10.5 w-full rounded-lg" />
            </Div>
          ))}
        </Div>
        <Div row className="mt-12 items-center gap-3">
          <Skeleton className="h-10 w-28 rounded-lg" />
          <Div className="flex-1" />
          <Skeleton className="h-10 w-32 rounded-lg" />
        </Div>
      </Div>
    </Div>
  </Div>
);

export interface WizardSubStep {
  label: string;
  invalid?: boolean;
}

export interface WizardStep {
  label: string;
  summary?: string;
  subSteps?: WizardSubStep[];
}

interface WizardPageProps {
  title: string;
  onBack: () => void;
  steps: WizardStep[];
  currentStep: number;
  onStepChange: (step: number) => void;
  currentSubStep?: number;
  onSubStepChange?: (subStep: number) => void;
  stepTitle: string;
  stepDescription?: string;
  hideStepCounter?: boolean;
  children: ReactNode;
  nextLabel: string;
  nextDisabled?: boolean;
  blockedHint?: string;
  onNext: () => void;
  onPrevious: () => void;
  submitting?: boolean;
  wide?: boolean;
}

const WizardPage = ({
  title,
  onBack,
  steps,
  currentStep,
  onStepChange,
  currentSubStep,
  onSubStepChange,
  stepTitle,
  stepDescription,
  hideStepCounter = false,
  children,
  nextLabel,
  nextDisabled,
  blockedHint,
  onNext,
  onPrevious,
  submitting,
  wide = false,
}: WizardPageProps) => {
  const { formatMessage } = useIntl();
  const pageMaxWidth = wide ? WIDE_PAGE_MAX_WIDTH : PAGE_MAX_WIDTH;
  const contentMaxWidth = wide ? undefined : CONTENT_MAX_WIDTH;

  const lastStepIndex = steps.length - 1;

  return (
    <Div column className="min-h-screen bg-canvas">
      <Div
        row
        className="sticky top-0 z-10 h-16 shrink-0 items-center border-b border-border bg-background px-8"
        data-slot="wizard-header"
      >
        <Div
          row
          className="mx-auto w-full items-center gap-3.5"
          style={{ maxWidth: pageMaxWidth - HEADER_GUTTER_COMPENSATION }}
        >
          <Button variant={ButtonVariant.GHOST} size="icon" onClick={onBack} aria-label={formatMessage({ id: 'back' })}>
            <ArrowLeftIcon fill="currentColor" />
          </Button>
          <Typography as="h4" size="lg" className="tracking-tight">
            {title}
          </Typography>
          <Div className="flex-1" />
        </Div>
      </Div>
      <Div
        grid
        className="mx-auto w-full flex-1 items-start gap-10 px-8 pt-12 pb-14"
        style={{ gridTemplateColumns: `${RAIL_WIDTH}px 1fr`, maxWidth: pageMaxWidth }}
      >
        <Div column className="sticky top-20 gap-5">
          <Div
            column
            className="rounded-2xl border border-border bg-background px-5 pt-5 pb-3"
            style={{ boxShadow: 'var(--shadow-1)' }}
          >
            {steps.map((step, index) => {
              const isDone = index < currentStep;
              const isCurrent = index === currentStep;
              const isClickable = isDone;
              const hasSummary = isDone && !isNil(step.summary) && step.summary !== '';
              const visibleSubSteps = isCurrent ? (step.subSteps ?? []) : [];
              return (
                <Div
                  key={step.label}
                  grid
                  className={cn('relative gap-3 pb-5', isClickable && 'cursor-pointer')}
                  style={{ gridTemplateColumns: `${BADGE_SIZE}px 1fr` }}
                  onClick={isClickable ? () => onStepChange(index) : undefined}
                >
                  {index < lastStepIndex && (
                    <Div
                      className={cn('absolute w-px', isDone ? 'bg-primary' : 'bg-border-strong')}
                      style={{ left: CONNECTOR_LEFT, top: CONNECTOR_TOP, bottom: CONNECTOR_BOTTOM }}
                    />
                  )}
                  <Badge
                    variant={isDone || isCurrent ? BadgeVariant.DEFAULT : BadgeVariant.OUTLINE}
                    className={cn(
                      'relative z-1 size-7 justify-center rounded-full p-0 text-xs font-bold',
                      !isDone && !isCurrent && 'border-border-strong text-fg-muted'
                    )}
                  >
                    {isDone ? <CheckIcon className="size-3.5 fill-current" /> : index + 1}
                  </Badge>
                  <Div column className="min-w-0 gap-0.5 pt-1">
                    <Typography
                      bold
                      className={cn(
                        isCurrent && 'text-fg-strong',
                        isDone && !isCurrent && 'text-fg',
                        !isDone && !isCurrent && 'text-fg-faint'
                      )}
                    >
                      {step.label}
                    </Typography>
                    {hasSummary && (
                      <Typography variant={TypographyVariant.MUTED} size="xs" className="truncate">
                        {step.summary}
                      </Typography>
                    )}
                    {!isEmpty(visibleSubSteps) && (
                      <Div column className="gap-0.5 pt-2">
                        {visibleSubSteps.map((subStep, subStepIndex) => {
                          const isCurrentSubStep = subStepIndex === currentSubStep;
                          return (
                            <Button
                              key={subStep.label}
                              variant={ButtonVariant.GHOST}
                              size="sm"
                              aria-current={isCurrentSubStep ? 'step' : undefined}
                              aria-invalid={subStep.invalid === true ? true : undefined}
                              className={cn(
                                'h-auto justify-start px-2 py-1.5 text-left font-normal whitespace-normal',
                                'text-fg-muted',
                                isCurrentSubStep && 'bg-accent-soft font-bold text-primary',
                                subStep.invalid === true &&
                                  'text-destructive aria-invalid:border-transparent aria-invalid:ring-0',
                                subStep.invalid === true && isCurrentSubStep && 'bg-loss-soft'
                              )}
                              onClick={() => onSubStepChange?.(subStepIndex)}
                            >
                              {subStep.label}
                            </Button>
                          );
                        })}
                      </Div>
                    )}
                  </Div>
                </Div>
              );
            })}
          </Div>
        </Div>
        <Div
          column
          className="min-w-0 rounded-2xl border border-border bg-background px-10 pt-9 pb-8"
          style={{ boxShadow: 'var(--shadow-1)' }}
        >
          <Div column className="gap-2" style={{ maxWidth: contentMaxWidth }}>
            {!hideStepCounter && (
              <Typography variant={TypographyVariant.PRIMARY} size="xs" bold className="tracking-wide">
                {formatMessage({ id: 'wizard.stepCounter' }, { current: currentStep + 1, total: steps.length })}
              </Typography>
            )}
            <Typography as="h4" className="leading-tight tracking-tight">
              {stepTitle}
            </Typography>
            {!isNil(stepDescription) && (
              <Typography variant={TypographyVariant.MUTED} className="leading-snug">
                {stepDescription}
              </Typography>
            )}
          </Div>
          <Div column className="gap-9 pt-8" style={{ maxWidth: contentMaxWidth }}>
            {children}
          </Div>
          <Div row className="mt-12 items-center gap-3">
            {currentStep > 0 && (
              <Button
                variant={ButtonVariant.OUTLINE}
                className="h-10 px-4.5"
                onClick={onPrevious}
                disabled={submitting}
              >
                {formatMessage({ id: 'previous' })}
              </Button>
            )}
            <Div className="flex-1" />
            {nextDisabled === true && !isNil(blockedHint) && (
              <Typography variant={TypographyVariant.MUTED} size="xs">
                {blockedHint}
              </Typography>
            )}
            <LoaderButton
              className="h-10 px-5.5"
              onClick={onNext}
              loading={submitting === true}
              disabled={nextDisabled === true || submitting === true}
            >
              {nextLabel}
            </LoaderButton>
          </Div>
        </Div>
      </Div>
    </Div>
  );
};

export { WizardPage, WizardPageSkeleton };
